/**
 * Expense Report Status Badge Component
 *
 * Displays a colored badge with icon for expense report status.
 * Colors indicate status: gray=draft, blue=submitted, amber=processing, green=paid, red=cancelled
 */

import { renderIcon } from '../utils/icons';
import type { ExpenseReportStatus } from '../types/expense-tracker';
import {
  EXPENSE_REPORT_STATUS_STYLES,
  formatExpenseReportStatus,
} from '../types/expense-tracker';

/**
 * Status badge options
 */
export interface StatusBadgeOptions {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show icon */
  showIcon?: boolean;
}

/**
 * Create an expense report status badge element
 */
/** Default style for unknown status values */
const DEFAULT_STATUS_STYLE = { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'helpCircle' };

export function createExpenseStatusBadge(
  status: ExpenseReportStatus,
  options: StatusBadgeOptions = {}
): HTMLElement {
  const { size = 'md', showIcon = true } = options;
  const style = EXPENSE_REPORT_STATUS_STYLES[status] || DEFAULT_STATUS_STYLE;

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-xs gap-1.5',
    lg: 'px-3 py-1.5 text-sm gap-2',
  };

  const iconSizes: Record<string, 'xs' | 'sm' | 'md'> = {
    sm: 'xs',
    md: 'xs',
    lg: 'sm',
  };

  const badge = document.createElement('span');
  badge.className = `inline-flex items-center rounded-full font-medium ${sizeClasses[size]} ${style.bg} ${style.text}`;

  const label = formatExpenseReportStatus(status);

  if (showIcon) {
    const iconSize = iconSizes[size] || 'xs';
    const icon = renderIcon(style.icon as Parameters<typeof renderIcon>[0], {
      size: iconSize,
    });
    badge.appendChild(icon);
  }

  const textSpan = document.createElement('span');
  textSpan.textContent = label;
  badge.appendChild(textSpan);

  return badge;
}

export default createExpenseStatusBadge;
